import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Upload, Image, Type, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Define all editable fields per page
const PAGE_SCHEMA = {
  home: {
    label: 'Accueil',
    sections: [
      {
        id: 'hero', label: 'Hero / Bannière',
        fields: [
          { key: 'hero_label', label: 'Sur-titre', type: 'text', default: 'Fabricant Français depuis 1957' },
          { key: 'hero_title', label: 'Titre (partie blanche)', type: 'text', default: 'Nous participons à la construction' },
          { key: 'hero_title_accent', label: 'Titre (partie rose)', type: 'text', default: "d'ouvrages techniques et durables." },
          { key: 'hero_subtitle', label: 'Sous-titre', type: 'textarea', default: "Spécialisé dans la pré-fabrication d'éléments structurels en béton, le groupe SOPREL met son expertise au service des acteurs du secteur du BTP." },
          { key: 'hero_video', label: 'URL Vidéo', type: 'text', default: 'https://customer-assets.emergentagent.com/job_soprel-build/artifacts/m3s6nzz3_SOPREL_V8_260401.mov' },
          { key: 'hero_bg_image', label: 'Image de fond (fallback)', type: 'image' },
        ]
      },
      {
        id: 'intro', label: 'Qui sommes-nous',
        fields: [
          { key: 'intro_label', label: 'Sur-titre', type: 'text', default: 'Qui sommes-nous' },
          { key: 'intro_title', label: 'Titre', type: 'text', default: 'Groupe SOPREL, innovation et performance' },
          { key: 'intro_text', label: 'Texte', type: 'textarea', default: "SOPREL se distingue également par sa capacité à répondre aux besoins spécifiques de ses clients, en offrant des solutions sur mesure, tout en mettant en avant des solutions environnementales et les exigences de sécurité les plus strictes." },
          { key: 'intro_image1', label: 'Photo gauche (grande)', type: 'image' },
          { key: 'intro_image2', label: 'Photo droite haut', type: 'image' },
          { key: 'intro_image3', label: 'Photo droite bas', type: 'image' },
        ]
      },
      {
        id: 'pillars', label: 'Les 3 piliers',
        fields: [
          { key: 'pillar1_title', label: 'Pilier 1 - Titre', type: 'text', default: 'Innover' },
          { key: 'pillar1_text', label: 'Pilier 1 - Texte', type: 'textarea', default: 'Pour un futur décarboné en proposant des solutions techniques et industrielles performantes qui répondent aux défis de la construction durable.' },
          { key: 'pillar2_title', label: 'Pilier 2 - Titre', type: 'text', default: 'Préserver' },
          { key: 'pillar2_text', label: 'Pilier 2 - Texte', type: 'textarea', default: "L'environnement, c'est favoriser les circuits courts, utiliser des matériaux recyclés et réduire notre empreinte carbone." },
          { key: 'pillar3_title', label: 'Pilier 3 - Titre', type: 'text', default: 'Accompagner' },
          { key: 'pillar3_text', label: 'Pilier 3 - Texte', type: 'textarea', default: "Nos clients depuis leurs choix techniques jusqu'à la livraison sur les chantiers. Conseil et réactivité font partie de l'ADN de l'entreprise." },
        ]
      },
      {
        id: 'cta', label: 'Appel à l\'action',
        fields: [
          { key: 'cta_title', label: 'Titre', type: 'text', default: 'Un projet de construction ?' },
          { key: 'cta_text', label: 'Texte', type: 'textarea', default: 'Nos équipes sont à votre disposition pour étudier vos besoins et vous proposer les solutions les plus adaptées.' },
        ]
      },
    ]
  },
  groupe: {
    label: 'Le Groupe',
    sections: [
      {
        id: 'hero', label: 'Hero / Bannière',
        fields: [
          { key: 'hero_title', label: 'Titre', type: 'text', default: 'SOPREL Groupe' },
          { key: 'hero_subtitle', label: 'Sous-titre', type: 'textarea', default: "Depuis 1957, nous concevons et fabriquons des éléments en béton précontraint de haute qualité pour les professionnels du BTP." },
        ]
      },
      {
        id: 'history', label: 'Histoire - Depuis 1957',
        fields: [
          { key: 'history_label', label: 'Sur-titre', type: 'text', default: 'Notre histoire' },
          { key: 'history_title', label: 'Titre', type: 'text', default: 'Depuis 1957' },
          { key: 'history_text', label: 'Texte', type: 'textarea', default: "C'est en 1957 que tout commence avec la création de SOPREL près de Paris. L'entreprise se spécialise rapidement dans la fabrication d'éléments en béton précontraint, répondant aux besoins croissants de la construction française d'après-guerre." },
          { key: 'history_image', label: 'Photo', type: 'image' },
        ]
      },
      {
        id: 'stats', label: 'Chiffres clés',
        fields: [
          { key: 'stat1_value', label: 'Stat 1 - Valeur', type: 'text', default: '+68' },
          { key: 'stat1_label', label: 'Stat 1 - Label', type: 'text', default: 'Ans' },
          { key: 'stat1_suffix', label: 'Stat 1 - Suffixe', type: 'text', default: "d'expérience" },
          { key: 'stat2_value', label: 'Stat 2 - Valeur', type: 'text', default: '3' },
          { key: 'stat2_label', label: 'Stat 2 - Label', type: 'text', default: 'Sites' },
          { key: 'stat2_suffix', label: 'Stat 2 - Suffixe', type: 'text', default: 'de production' },
          { key: 'stat3_value', label: 'Stat 3 - Valeur', type: 'text', default: 'Brevet' },
          { key: 'stat3_label', label: 'Stat 3 - Label', type: 'text', default: 'DAP WIDE' },
          { key: 'stat3_suffix', label: 'Stat 3 - Suffixe', type: 'text', default: 'Innovation SOPREL' },
          { key: 'stat4_value', label: 'Stat 4 - Valeur', type: 'text', default: 'Réalisations' },
          { key: 'stat4_label', label: 'Stat 4 - Label', type: 'text', default: 'France' },
          { key: 'stat4_suffix', label: 'Stat 4 - Suffixe', type: 'text', default: 'entière' },
        ]
      },
      {
        id: 'locations', label: 'Nos implantations',
        fields: [
          { key: 'locations_label', label: 'Sur-titre', type: 'text', default: 'Nos implantations' },
          { key: 'locations_title', label: 'Titre', type: 'text', default: '4 établissements à votre service' },
          { key: 'locations_subtitle', label: 'Sous-titre', type: 'textarea', default: 'Une couverture nationale grâce à nos sites de production stratégiquement implantés.' },
        ]
      },
      {
        id: 'cta', label: 'Appel à l\'action',
        fields: [
          { key: 'cta_title', label: 'Titre', type: 'text', default: "Rejoignez l'aventure SOPREL" },
          { key: 'cta_text', label: 'Texte', type: 'textarea', default: "Nous sommes toujours à la recherche de talents passionnés. Découvrez nos offres d'emploi." },
        ]
      },
    ]
  },
  engagements: {
    label: 'Engagements',
    sections: [
      {
        id: 'hero', label: 'Hero / Bannière',
        fields: [
          { key: 'hero_title', label: 'Titre', type: 'text', default: 'Nos Engagements' },
          { key: 'hero_subtitle', label: 'Sous-titre', type: 'textarea', default: "Qualité, sécurité et respect de l'environnement sont au cœur de notre démarche industrielle." },
        ]
      },
      {
        id: 'expertise', label: 'Notre expertise',
        fields: [
          { key: 'expertise_label', label: 'Sur-titre', type: 'text', default: 'Savoir-faire' },
          { key: 'expertise_title', label: 'Titre', type: 'text', default: 'Notre expertise à votre service' },
          { key: 'expertise_text1', label: 'Paragraphe 1', type: 'textarea', default: "Fort de plus de 68 ans d'expérience, SOPREL maîtrise l'ensemble de la chaîne de production." },
          { key: 'expertise_text2', label: 'Paragraphe 2', type: 'textarea', default: "De la conception à la livraison, chaque étape est rigoureusement contrôlée pour garantir une qualité optimale." },
          { key: 'expertise_image_main', label: 'Photo principale', type: 'image' },
          { key: 'expertise_image_small', label: 'Photo secondaire', type: 'image' },
        ]
      },
      {
        id: 'quality', label: 'Qualité et Sécurité',
        fields: [
          { key: 'quality_title', label: 'Titre', type: 'text', default: 'Qualité et Sécurité' },
          { key: 'quality_text', label: 'Texte', type: 'textarea', default: "La qualité et la sécurité sont les fondements de notre activité industrielle." },
          { key: 'quality_image1', label: 'Photo 1', type: 'image' },
          { key: 'quality_image2', label: 'Photo 2', type: 'image' },
          { key: 'quality_image3', label: 'Photo 3', type: 'image' },
          { key: 'quality_image4', label: 'Photo 4', type: 'image' },
        ]
      },
      {
        id: 'carbon', label: 'Empreinte carbone',
        fields: [
          { key: 'carbon_title_l1', label: 'Titre Ligne 1', type: 'text', default: 'DIMINUER' },
          { key: 'carbon_title_l2', label: 'Titre Ligne 2', type: 'text', default: "L'EMPREINTE" },
          { key: 'carbon_title_l3', label: 'Titre Ligne 3 (rose)', type: 'text', default: 'CARBONE' },
        ]
      },
      {
        id: 'values', label: 'Nos valeurs',
        fields: [
          { key: 'values_title', label: 'Titre', type: 'text', default: 'Partageons nos valeurs' },
          { key: 'values_text', label: 'Texte', type: 'textarea', default: "Nos valeurs guident chacune de nos actions et définissent notre culture d'entreprise." },
        ]
      },
    ]
  },
  realisations: {
    label: 'Réalisations',
    sections: [
      {
        id: 'hero', label: 'Hero / Bannière',
        fields: [
          { key: 'hero_title', label: 'Titre', type: 'text', default: 'Nos Réalisations' },
          { key: 'hero_subtitle', label: 'Sous-titre', type: 'textarea', default: "Découvrez les projets emblématiques réalisés par SOPREL Groupe à travers toute la France." },
        ]
      },
      {
        id: 'featured', label: 'Section vedette',
        fields: [
          { key: 'featured_label', label: 'Sur-titre', type: 'text', default: 'Projets phares' },
          { key: 'featured_title', label: 'Titre', type: 'text', default: 'Réalisations emblématiques' },
        ]
      },
      {
        id: 'cta', label: 'Appel à l\'action',
        fields: [
          { key: 'cta_title', label: 'Titre', type: 'text', default: 'Votre projet mérite le meilleur' },
          { key: 'cta_text', label: 'Texte', type: 'textarea', default: "Confiez-nous votre projet et bénéficiez de notre expertise en béton précontraint." },
        ]
      },
    ]
  },
  recrutement: {
    label: 'Recrutement',
    sections: [
      {
        id: 'hero', label: 'Hero / Bannière',
        fields: [
          { key: 'hero_title', label: 'Titre', type: 'text', default: 'Rejoignez-nous' },
          { key: 'hero_subtitle', label: 'Sous-titre', type: 'textarea', default: "Construisez votre avenir avec SOPREL Groupe." },
        ]
      },
      {
        id: 'advantages', label: 'Avantages',
        fields: [
          { key: 'advantages_title', label: 'Titre', type: 'text', default: 'Une entreprise où il fait bon travailler' },
          { key: 'advantages_text', label: 'Texte', type: 'textarea', default: "Chez SOPREL, nous croyons que la réussite de l'entreprise passe par l'épanouissement de nos collaborateurs." },
        ]
      },
      {
        id: 'spontaneous', label: 'Candidature spontanée',
        fields: [
          { key: 'spontaneous_title', label: 'Titre', type: 'text', default: 'Candidature spontanée' },
          { key: 'spontaneous_text', label: 'Texte', type: 'textarea', default: "Aucune offre ne correspond à votre profil ? Envoyez-nous votre candidature spontanée." },
        ]
      },
    ]
  },
  contact: {
    label: 'Contact',
    sections: [
      {
        id: 'hero', label: 'Hero / Bannière',
        fields: [
          { key: 'hero_title', label: 'Titre', type: 'text', default: 'Parlons de votre projet' },
          { key: 'hero_subtitle', label: 'Sous-titre', type: 'textarea', default: "Notre équipe est à votre écoute pour répondre à toutes vos questions et vous accompagner dans vos projets." },
        ]
      },
      {
        id: 'form', label: 'Formulaire',
        fields: [
          { key: 'form_title', label: 'Titre formulaire', type: 'text', default: 'Envoyez-nous un message' },
          { key: 'info_title', label: 'Titre infos contact', type: 'text', default: 'Informations de contact' },
        ]
      },
      {
        id: 'map', label: 'Carte',
        fields: [
          { key: 'map_title', label: 'Titre carte', type: 'text', default: 'Retrouvez-nous en France' },
          { key: 'map_subtitle', label: 'Sous-titre carte', type: 'text', default: '4 sites à votre service sur le territoire national' },
        ]
      },
    ]
  },
};

const ImageUpload = ({ value, onChange, fieldKey }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/api/admin/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = data.url.startsWith('http') ? data.url : `${API}${data.url}`;
      onChange(url);
      toast.success('Image uploadée');
    } catch (err) {
      toast.error("Erreur lors de l'upload");
    }
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      {value && (
        <div className="w-32 h-24 rounded border overflow-hidden bg-slate-100">
          <img src={value} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL de l'image"
          className="flex-1 px-3 py-2 border rounded text-sm bg-white"
          data-testid={`field-${fieldKey}`}
        />
        <label className={`cursor-pointer px-3 py-2 rounded text-sm font-medium flex items-center gap-1 ${uploading ? 'bg-slate-300' : 'bg-[#0078b7] text-white hover:bg-[#0078b7]/90'}`}>
          <Upload className="w-4 h-4" />
          {uploading ? '...' : 'Upload'}
          <input type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} />
        </label>
      </div>
    </div>
  );
};

const SectionEditor = ({ section, content, onChange }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid={`section-${section.id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <span className="font-semibold text-slate-800">{section.label}</span>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {open && (
        <div className="p-4 space-y-4 border-t">
          {section.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {field.type === 'image' && <Image className="inline w-4 h-4 mr-1 text-[#0078b7]" />}
                {field.type === 'text' && <Type className="inline w-4 h-4 mr-1 text-slate-400" />}
                {field.type === 'textarea' && <FileText className="inline w-4 h-4 mr-1 text-slate-400" />}
                {field.label}
              </label>
              {field.type === 'image' ? (
                <ImageUpload
                  value={content[field.key] || ''}
                  onChange={(val) => onChange(field.key, val)}
                  fieldKey={field.key}
                />
              ) : field.type === 'textarea' ? (
                <textarea
                  value={content[field.key] ?? field.default ?? ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded text-sm bg-white resize-y"
                  data-testid={`field-${field.key}`}
                />
              ) : (
                <input
                  type="text"
                  value={content[field.key] ?? field.default ?? ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm bg-white"
                  data-testid={`field-${field.key}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminPageContent() {
  const [activePage, setActivePage] = useState('home');
  const [allContent, setAllContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/page-content`, { withCredentials: true });
        const map = {};
        data.forEach(doc => { map[doc.page] = doc.content || {}; });
        setAllContent(map);
      } catch (e) {
        console.error('Error fetching content:', e);
      }
      setLoading(false);
    };
    fetchAll();
  }, []);

  const currentContent = allContent[activePage] || {};
  const schema = PAGE_SCHEMA[activePage];

  const handleFieldChange = useCallback((key, value) => {
    setAllContent(prev => ({
      ...prev,
      [activePage]: { ...(prev[activePage] || {}), [key]: value }
    }));
  }, [activePage]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/page-content/${activePage}`, {
        content: allContent[activePage] || {}
      }, { withCredentials: true });
      toast.success(`Page "${schema.label}" sauvegardée`);
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0078b7]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-page-content">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Contenu des pages</h1>
          <p className="text-sm text-slate-500 mt-1">Modifiez les textes et images de chaque page du site</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0078b7] hover:bg-[#0078b7]/90 text-white"
          data-testid="save-page-content-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      {/* Page Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 bg-slate-100 p-1 rounded-lg" data-testid="page-tabs">
        {Object.entries(PAGE_SCHEMA).map(([key, page]) => (
          <button
            key={key}
            onClick={() => setActivePage(key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activePage === key 
                ? 'bg-white text-[#0078b7] shadow-sm' 
                : 'text-slate-600 hover:text-slate-800 hover:bg-white/50'
            }`}
            data-testid={`tab-${key}`}
          >
            {page.label}
          </button>
        ))}
      </div>

      {/* Section Editors */}
      <div className="space-y-4">
        {schema.sections.map((section) => (
          <SectionEditor
            key={section.id}
            section={section}
            content={currentContent}
            onChange={handleFieldChange}
          />
        ))}
      </div>
    </div>
  );
}
